import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useWebSocket } from '../hooks/useWebSocket';

// ─── Constantes ───────────────────────────────────────────────────────────────
const CANAL = {
  whatsapp: { icon: '📱', color: '#22c55e', label: 'WhatsApp' },
  instagram: { icon: '📸', color: '#ec4899', label: 'Instagram' },
  facebook: { icon: '💬', color: '#3b82f6', label: 'Facebook' },
};

function getCanalInfo(canal) {
  return CANAL[canal?.toLowerCase()] || { icon: '💬', color: '#94a3b8', label: canal };
}

function fmtTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  });
}

function fmtDate(d) {
  if (!d) return '';
  const tz = 'America/Bogota';
  const dt = new Date(d);
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: tz });
  const dtStr = dt.toLocaleDateString('en-CA', { timeZone: tz });
  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  const yestStr = yest.toLocaleDateString('en-CA', { timeZone: tz });
  if (dtStr === todayStr) return 'Hoy';
  if (dtStr === yestStr) return 'Ayer';
  return dt.toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short', timeZone: tz });
}

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function isExpired(lastAt) {
  if (!lastAt) return false;
  return (Date.now() - new Date(lastAt).getTime()) > 24 * 60 * 60 * 1000;
}

function renderWA(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {line.split(/(\*[^*]+\*)/g).map((part, j) =>
        part.startsWith('*') && part.endsWith('*') && part.length > 2
          ? <strong key={j}>{part.slice(1, -1)}</strong>
          : part
      )}
    </React.Fragment>
  ));
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ChatPage() {
  const { user, logout } = useAuth();
  const { apiFetch } = useApi();

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState({ desde: '', hasta: '' });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  const [tagPickerFor, setTagPickerFor] = useState(null);
  const [unread, setUnread] = useState({});
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const messagesEndRef = useRef(null);
  const selectedRef = useRef(null);
  const textareaRef = useRef(null);
  const [togglingModo, setTogglingModo] = useState(false);
  const [editingNombre, setEditingNombre] = useState(false);
  const [nombreDraft, setNombreDraft] = useState('');
  const [retomando, setRetomando] = useState(false);
  const [iniciandoId, setIniciandoId] = useState(null);

  const [contactos, setContactos] = useState([]);
  const [view, setView] = useState('chats'); // 'chats' | 'contactos'
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContactNumero, setNewContactNumero] = useState('');
  const [newContactNombre, setNewContactNombre] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  selectedRef.current = selected;

  // ─── Cargar datos ─────────────────────────────────────────────────────────
  const loadConversations = useCallback(async (df) => {
    try {
      const filters = df || dateFilter;
      const params = new URLSearchParams();
      if (filters.desde) params.set('desde', filters.desde);
      if (filters.hasta) params.set('hasta', filters.hasta);
      const qs = params.toString();
      const data = await apiFetch(`/api/conversations${qs ? '?' + qs : ''}`);
      setConversations(data);
    } catch { }
  }, [apiFetch, dateFilter]);

  function applyDateFilter(desde, hasta) {
    const nf = { desde, hasta };
    setDateFilter(nf);
    loadConversations(nf);
    setShowDatePicker(false);
  }
  function clearDateFilter() { applyDateFilter('', ''); }

  const loadStats = useCallback(async () => {
    try {
      const data = await apiFetch('/api/stats');
      setStats(data);
    } catch { }
  }, [apiFetch]);

  const loadTags = useCallback(async () => {
    try {
      const data = await apiFetch('/api/tags');
      setAllTags(data);
    } catch { }
  }, [apiFetch]);

  const loadContactos = useCallback(async () => {
    try {
      const data = await apiFetch('/api/contactos');
      setContactos(data);
    } catch { }
  }, [apiFetch]);


  const loadMessages = useCallback(async (session_id) => {
    setLoadingMsgs(true);
    try {
      const data = await apiFetch(`/api/conversations/${session_id}/messages`);
      setMessages(data);
    } catch { } finally {
      setLoadingMsgs(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadConversations();
    loadStats();
    loadTags();
    loadContactos();
    const t = setInterval(() => { loadConversations(); loadStats(); }, 15000);
    return () => clearInterval(t);
  }, [loadConversations, loadStats, loadTags, loadContactos]);

  useEffect(() => {
    if (selected) loadMessages(selected.session_id);
  }, [selected, loadMessages]);

  // ─── WebSocket ────────────────────────────────────────────────────────────
  const wsStatus = useWebSocket(useCallback((msg) => {
    if (msg.type !== 'new_message') return;
    const nm = msg.data;

    setConversations(prev => {
      const idx = prev.findIndex(c => c.session_id === nm.session_id);
      const updated = idx >= 0 ? [...prev] : [...prev];
      if (idx >= 0) {
        updated[idx] = { ...updated[idx], last_message: nm.content, last_type: nm.type, last_at: nm.created_at };
        const [conv] = updated.splice(idx, 1);
        return [conv, ...updated];
      }
      return prev;
    });

    if (selectedRef.current?.session_id === nm.session_id) {
      setMessages(prev => [...prev, nm]);
    } else {
      setUnread(prev => ({ ...prev, [nm.session_id]: (prev[nm.session_id] || 0) + 1 }));
    }
  }, []));

  // ─── Auto scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Seleccionar conversación ─────────────────────────────────────────────
  function selectConv(conv) {
    setSelected(conv);
    setUnread(prev => ({ ...prev, [conv.session_id]: 0 }));
  }

  // ─── Enviar mensaje ───────────────────────────────────────────────────────
  async function sendMessage() {
    if (!inputText.trim() || !selected || sending) return;
    setSending(true);
    try {
      await apiFetch('/api/send', {
        method: 'POST',
        body: JSON.stringify({
          session_id: selected.session_id,
          text: inputText.trim(),
          canal: selected.canal,
          phone_number: selected.user_id,
        }),
      });
      setInputText('');
      textareaRef.current?.focus();
    } catch (e) {
      alert('Error al enviar: ' + e.message);
    } finally {
      setSending(false);
    }
  }

  async function toggleModo() {
    if (!selected || togglingModo) return;
    const nuevoModo = selected.modo_atencion === 'bot' ? 'humano' : 'bot';
    setTogglingModo(true);
    try {
      await apiFetch(`/api/conversations/${selected.session_id}/set-mode`, {
        method: 'POST',
        body: JSON.stringify({ modo: nuevoModo, whatsapp_numero: selected.user_id }),
      });
      setSelected(prev => ({ ...prev, modo_atencion: nuevoModo }));
      setConversations(prev => prev.map(c =>
        c.session_id === selected.session_id ? { ...c, modo_atencion: nuevoModo } : c
      ));
    } catch (e) {
      alert('Error al cambiar modo: ' + e.message);
    } finally {
      setTogglingModo(false);
    }
  }

  async function retomarConversacion() {
    if (!selected || retomando) return;
    setRetomando(true);
    try {
      await apiFetch(`/api/conversations/${selected.session_id}/retomar`, { method: 'POST' });
      loadMessages(selected.session_id);
      loadConversations();
    } catch (e) {
      alert('Error al retomar: ' + e.message);
    } finally {
      setRetomando(false);
    }
  }

  async function addContacto() {
    if (!newContactNumero.trim()) return;
    setSavingContact(true);
    try {
      await apiFetch('/api/contactos', {
        method: 'POST',
        body: JSON.stringify({ whatsapp_numero: newContactNumero.trim(), nombre: newContactNombre.trim() }),
      });
      setNewContactNumero('');
      setNewContactNombre('');
      setShowAddContact(false);
      loadContactos();
    } catch (e) {
      alert('Error al agregar contacto: ' + e.message);
    } finally {
      setSavingContact(false);
    }
  }

  async function iniciarConversacion(contactoId) {
  setIniciandoId(contactoId);
  try {
    const res = await apiFetch(`/api/contactos/${contactoId}/iniciar`, { method: 'POST' });
    loadConversations();
    setView('chats');
  } catch (e) {
    alert('Error al iniciar conversación: ' + e.message);
  } finally {
    setIniciandoId(null);
  }
}

  async function saveNombre(session_id, nombre) {
    try {
      await apiFetch(`/api/conversations/${session_id}/nombre`, {
        method: 'POST',
        body: JSON.stringify({ nombre }),
      });
      setConversations(prev => prev.map(c =>
        c.session_id === session_id ? { ...c, nombre: nombre?.trim() || null } : c
      ));
      setSelected(prev => prev ? { ...prev, nombre: nombre?.trim() || null } : prev);
    } catch (e) {
      alert('Error al guardar nombre: ' + e.message);
    }
  }

  // ─── Borrar conversación completa ─────────────────────────────────────────
  async function deleteConversation(session_id) {
    if (!window.confirm('¿Borrar toda esta conversación? Esta acción no se puede deshacer.')) return;
    try {
      await apiFetch(`/api/conversations/${session_id}`, { method: 'DELETE' });
      setConversations(prev => prev.filter(c => c.session_id !== session_id));
      if (selected?.session_id === session_id) {
        setSelected(null);
        setMessages([]);
      }
    } catch (e) {
      alert('Error al borrar: ' + e.message);
    }
  }

  // ─── Borrar mensaje individual ────────────────────────────────────────────
  async function deleteMessage(session_id, msg_id) {
    if (!window.confirm('¿Borrar este mensaje?')) return;
    try {
      await apiFetch(`/api/conversations/${session_id}/messages/${msg_id}`, { method: 'DELETE' });
      setMessages(prev => prev.filter(m => m.id !== msg_id));
    } catch (e) {
      alert('Error al borrar mensaje: ' + e.message);
    }
  }

  async function addTag(session_id, tag_id) {
    try {
      await apiFetch(`/api/conversations/${session_id}/tags`, {
        method: 'POST',
        body: JSON.stringify({ tag_id }),
      });
      setConversations(prev => prev.map(c => {
        if (c.session_id !== session_id) return c;
        const tag = allTags.find(t => t.id === tag_id);
        if (c.tags?.some(t => t.id === tag_id)) return c;
        return { ...c, tags: [...(c.tags || []), tag] };
      }));
    } catch (e) {
      alert('Error al etiquetar: ' + e.message);
    }
    setTagPickerFor(null);
  }

  async function removeTag(session_id, tag_id) {
    try {
      await apiFetch(`/api/conversations/${session_id}/tags/${tag_id}`, { method: 'DELETE' });
      setConversations(prev => prev.map(c => {
        if (c.session_id !== session_id) return c;
        return { ...c, tags: (c.tags || []).filter(t => t.id !== tag_id) };
      }));
    } catch (e) {
      alert('Error al quitar etiqueta: ' + e.message);
    }
  }

  // ─── Filtrar ──────────────────────────────────────────────────────────────
  const filtered = conversations.filter(c => {
    const matchesSearch = !search ||
      c.user_id?.includes(search) ||
      c.canal?.toLowerCase().includes(search.toLowerCase()) ||
      c.last_message?.toLowerCase().includes(search.toLowerCase());
    const matchesTag = !tagFilter || c.tags?.some(t => t.id === tagFilter);
    return matchesSearch && matchesTag;
  });

  // ─── Agrupar mensajes por fecha ───────────────────────────────────────────
  const grouped = [];
  let lastDate = null;
  messages.forEach(msg => {
    const d = fmtDate(msg.created_at);
    if (d !== lastDate) { grouped.push({ _type: 'date', label: d }); lastDate = d; }
    grouped.push(msg);
  });

  const cInfo = selected ? getCanalInfo(selected.canal) : null;
  const expired = selected ? isExpired(selected.last_at) : false;

  return (
    <div style={s.root}>
      {/* ── Sidebar ── */}
      <aside style={s.sidebar}>
        {/* Header sidebar */}
        <div style={s.sidebarTop}>
          <div style={s.brand}>
            <div style={s.brandLogo}>THE NEXUS</div>
            <div style={s.brandInfo}>
              <div style={s.brandName}>Portal Atención</div>
              <div style={s.brandUser}>👤 {user?.username}</div>
            </div>
            <button onClick={logout} style={s.logoutBtn} title="Cerrar sesión">⏻</button>
          </div>

          {/* Selector de vista */}
          <div style={s.viewTabs}>
            <button
              onClick={() => setView('chats')}
              style={{ ...s.viewTab, ...(view === 'chats' ? s.viewTabActive : {}) }}
            >💬 Chats</button>
            <button
              onClick={() => setView('contactos')}
              style={{ ...s.viewTab, ...(view === 'contactos' ? s.viewTabActive : {}) }}
            >👥 Contactos</button>
          </div>

          {view === 'chats' && (
            <>
              {/* Stats rápidas */}
              {stats && (
                <div style={s.statsGrid}>
                  <StatBox label="Chats" value={stats.total_conversations} color="#7c3aed" />
                  <StatBox label="Mensajes" value={stats.total_messages} color="#ec4899" />
                  <StatBox label="Hoy" value={stats.active_today} color="#22c55e" />
                </div>
              )}

              {/* Search */}
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ ...s.search, flex: 1 }}>
                  <span style={s.searchIco}>🔍</span>
                  <input style={s.searchInput} placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} />
                  {search && <button onClick={() => setSearch('')} style={s.searchClear}>✕</button>}
                </div>
                <button onClick={() => setShowDatePicker(v => !v)}
                  style={{ ...s.dateBtn, ...(dateFilter.desde || dateFilter.hasta ? s.dateBtnActive : {}) }}
                  title="Filtrar por fecha">📅</button>
              </div>

              {showDatePicker && (
                <div style={s.datePanel}>
                  <div style={s.dateRow}>
                    <label style={s.dateLabel}>Desde</label>
                    <input type="date" style={s.dateInput} value={dateFilter.desde}
                      onChange={e => setDateFilter(f => ({ ...f, desde: e.target.value }))} />
                  </div>
                  <div style={s.dateRow}>
                    <label style={s.dateLabel}>Hasta</label>
                    <input type="date" style={s.dateInput} value={dateFilter.hasta}
                      onChange={e => setDateFilter(f => ({ ...f, hasta: e.target.value }))} />
                  </div>
                  <div style={s.dateActions}>
                    <button style={s.dateApplyBtn} onClick={() => applyDateFilter(dateFilter.desde, dateFilter.hasta)}>Aplicar</button>
                    <button style={s.dateClearBtn} onClick={clearDateFilter}>Limpiar</button>
                  </div>
                </div>
              )}

              {(dateFilter.desde || dateFilter.hasta) && !showDatePicker && (
                <div style={s.dateActiveChip} onClick={() => setShowDatePicker(true)}>
                  📅 {dateFilter.desde || '…'} → {dateFilter.hasta || '…'}
                  <span onClick={e => { e.stopPropagation(); clearDateFilter(); }} style={{ marginLeft: 6 }}>✕</span>
                </div>
              )}

              {/* Filtro por etiqueta */}
              <div style={s.tagFilterRow}>
                <button
                  onClick={() => setTagFilter(null)}
                  style={{ ...s.tagFilterChip, ...(tagFilter === null ? s.tagFilterChipActive : {}) }}
                >
                  Todas
                </button>
                {allTags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => setTagFilter(tagFilter === tag.id ? null : tag.id)}
                    style={{
                      ...s.tagFilterChip,
                      ...(tagFilter === tag.id ? { background: tag.color + '30', border: `1px solid ${tag.color}` } : {}),
                    }}
                  >
                    {tag.emoji} {tag.name}
                  </button>
                ))}
              </div>
            </>
          )}

          {view === 'contactos' && (
            <button onClick={() => setShowAddContact(true)} style={s.addContactBtn}>
              + Agregar contacto
            </button>
          )}
        </div>

        {/* Lista de conversaciones O contactos */}
        <div style={s.convList}>
          {view === 'chats' ? (
            <>
              {filtered.length === 0 && (
                <div style={s.emptyList}>
                  {search ? 'Sin resultados' : 'Sin conversaciones'}
                </div>
              )}
              {filtered.map(conv => {
                const ci = getCanalInfo(conv.canal);
                const isActive = selected?.session_id === conv.session_id;
                const badge = unread[conv.session_id] || 0;
                return (
                  <div
                    key={conv.session_id}
                    onClick={() => selectConv(conv)}
                    style={{
                      ...s.convItem,
                      background: isActive
                        ? 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(236,72,153,0.08))'
                        : 'transparent',
                      borderLeft: `3px solid ${isActive ? ci.color : 'transparent'}`,
                    }}
                  >
                    <div style={{ ...s.convAva, background: ci.color + '18', border: `1.5px solid ${ci.color}30` }}>
                      <span style={{ fontSize: 20 }}>{ci.icon}</span>
                    </div>
                    <div style={s.convBody}>
                      <div style={s.convRow1}>
                        <span style={s.convName}>{conv.user_id}{conv.nombre ? ` - ${conv.nombre}` : ''}</span>
                        <span style={s.convTime}>{timeAgo(conv.last_at)}</span>
                      </div>
                      <div style={s.convRow2}>
                        <span style={s.convPrev}>
                          {conv.last_type === 'ai' ? '🤖 ' : conv.last_type === 'asesor' ? '✍️ ' : ''}
                          {(conv.last_message || '').slice(0, 44)}{(conv.last_message || '').length > 44 ? '…' : ''}
                        </span>
                        {badge > 0 && <span style={s.badge}>{badge}</span>}
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); deleteConversation(conv.session_id); }}
                        style={s.deleteConvBtn}
                        title="Borrar conversación"
                      >🗑</button>
                      <div style={{ ...s.canalTag, color: ci.color }}>
                        {ci.label} · {conv.message_count} msgs
                      </div>

                      {/* Tags asignados */}
                      <div style={s.convTagsRow}>
                        {(conv.tags || []).map(tag => (
                          <span
                            key={tag.id}
                            onClick={e => { e.stopPropagation(); removeTag(conv.session_id, tag.id); }}
                            style={{ ...s.convTagChip, background: tag.color + '25', border: `1px solid ${tag.color}50` }}
                            title="Clic para quitar"
                          >
                            {tag.emoji} {tag.name}
                          </span>
                        ))}
                        <span
                          onClick={e => { e.stopPropagation(); setTagPickerFor(tagPickerFor === conv.session_id ? null : conv.session_id); }}
                          style={s.addTagBtn}
                          title="Añadir etiqueta"
                        >+ etiqueta</span>

                        {tagPickerFor === conv.session_id && (
                          <div style={s.tagPicker} onClick={e => e.stopPropagation()}>
                            {allTags
                              .filter(t => !conv.tags?.some(ct => ct.id === t.id))
                              .map(t => (
                                <div key={t.id} onClick={() => addTag(conv.session_id, t.id)} style={s.tagPickerItem}>
                                  {t.emoji} {t.name}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              {contactos.length === 0 && (
                <div style={s.emptyList}>Sin contactos aún</div>
              )}
              {contactos.map(c => (
                <div key={c.id} style={s.contactItem}>
                  <div style={s.contactAva}>👤</div>
                  <div style={s.contactInfo}>
                    <div style={s.contactName} title={`${c.whatsapp_numero}${c.nombre ? ` - ${c.nombre}` : ''}`}>
                      {c.whatsapp_numero}{c.nombre ? ` - ${c.nombre}` : ''}
                    </div>
                    <div style={s.contactDate}>Agregado {fmtDate(c.creado_en)}</div>
                  </div>
                  <button
                    onClick={() => iniciarConversacion(c.id)}
                    disabled={iniciandoId === c.id}
                    style={s.iniciarBtn}
                  >
                    {iniciandoId === c.id ? '...' : '💬 Iniciar'}
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        {/* WS status */}
        <div style={s.wsBar}>
          <span style={{ ...s.wsDot, background: wsStatus === 'connected' ? '#22c55e' : wsStatus === 'connecting' ? '#f59e0b' : '#ef4444' }} />
          <span style={s.wsLabel}>
            {wsStatus === 'connected' ? 'Tiempo real activo' : wsStatus === 'connecting' ? 'Conectando...' : 'Sin conexión'}
          </span>
        </div>
      </aside>

      {/* ── Panel principal ── */}
      <main style={s.main}>
        {!selected ? (
          <EmptyState />
        ) : (
          <>
            {/* Chat header */}
            <div style={s.chatHeader}>
              <div style={{ ...s.chatCanalBadge, background: cInfo.color + '15', border: `1.5px solid ${cInfo.color}30` }}>
                <span style={{ fontSize: 20 }}>{cInfo.icon}</span>
                <span style={{ color: cInfo.color, fontWeight: 700, fontSize: 12 }}>{cInfo.label}</span>
              </div>
              <div style={s.chatHeaderMid}>
                <div style={s.chatHeaderName}>
                  {editingNombre ? (
                    <input
                      autoFocus
                      style={s.nombreInput}
                      value={nombreDraft}
                      onChange={e => setNombreDraft(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { saveNombre(selected.session_id, nombreDraft); setEditingNombre(false); }
                        if (e.key === 'Escape') setEditingNombre(false);
                      }}
                      onBlur={() => { saveNombre(selected.session_id, nombreDraft); setEditingNombre(false); }}
                      placeholder="Nombre del contacto"
                    />
                  ) : (
                    <span
                      onClick={() => { setNombreDraft(selected.nombre || ''); setEditingNombre(true); }}
                      style={{ cursor: 'pointer' }}
                      title="Clic para editar nombre"
                    >
                      {selected.user_id}{selected.nombre ? ` - ${selected.nombre}` : ' + agregar nombre'}
                    </span>
                  )}
                </div>
                <div style={s.chatHeaderSub}>{selected.message_count} mensajes · session: {selected.session_id}</div>
              </div>
              <div style={s.chatHeaderRight}>
                <button
                  onClick={toggleModo}
                  disabled={togglingModo}
                  style={{
                    ...s.modoTag,
                    cursor: 'pointer',
                    border: 'none',
                    background: selected.modo_atencion === 'bot' ? 'rgba(124,58,237,0.15)' : 'rgba(34,197,94,0.15)',
                    color: selected.modo_atencion === 'bot' ? '#a78bfa' : '#34d399',
                    boxShadow: selected.modo_atencion === 'bot' ? '0 0 0 1px rgba(124,58,237,0.3)' : '0 0 0 1px rgba(34,197,94,0.3)',
                    opacity: togglingModo ? 0.5 : 1,
                  }}
                  title="Click para cambiar modo"
                >
                  {selected.modo_atencion === 'bot' ? '🤖 Bot activo' : '👤 Humano activo'}
                </button>
              </div>
            </div>

            {/* Mensajes */}
            <div style={s.messages}>
              {loadingMsgs && (
                <div style={s.loadingMsgs}>Cargando historial...</div>
              )}
              {grouped.map((item, i) => {
                if (item._type === 'date') {
                  return <DateSep key={`d-${i}`} label={item.label} />;
                }
                const isUser = item.type === 'human' && !item.content?.startsWith('[ASESOR]');
                const isAsesor = item.content?.startsWith('[ASESOR]');
                const isAI = item.type === 'ai';
                // Limpiar prefijos que vienen de n8n/helpers
                const cleanContent = (item.content || '')
                  .replace(/^Texto del mensaje:\s*/i, '')
                  .replace(/^\[ASESOR\]\s*/, '');
                const text = cleanContent;
                const ci2 = getCanalInfo(item.canal);

                return (
                  <div key={item.id || i} style={{ ...s.msgRow, justifyContent: isUser ? 'flex-start' : 'flex-end' }}>
                    {isUser && (
                      <div style={{ ...s.avatar, background: ci2.color + '20', fontSize: 16 }}>{ci2.icon}</div>
                    )}
                    <div style={{
                      ...s.bubble,
                      ...(isUser ? s.bubbleUser : isAI ? s.bubbleAI : s.bubbleAsesor),
                    }}>
                      {isUser && (
                        <div style={{ ...s.bubbleWho, color: ci2.color }}>
                          {ci2.icon} Cliente
                        </div>
                      )}
                      {!isUser && (
                        <div style={{ ...s.bubbleWho, color: isAI ? '#a78bfa' : '#34d399' }}>
                          {isAI ? '🤖 AlejaIA' : '✍️ Asesor'}
                        </div>
                      )}
                      <div style={s.bubbleTxt}>{renderWA(text)}</div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 5 }}>
                        <div style={s.bubbleTs}>{fmtTime(item.created_at)}</div>
                        {item.id && (
                          <button
                            onClick={() => deleteMessage(selected.session_id, item.id)}
                            style={s.deleteMsgBtn}
                            title="Borrar mensaje"
                          >🗑</button>
                        )}
                      </div>
                    </div>
                    {!isUser && (
                      <div style={{ ...s.avatar, background: isAI ? 'rgba(124,58,237,0.2)' : 'rgba(34,197,94,0.2)', fontSize: 14 }}>
                        {isAI ? '🤖' : '👤'}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {expired ? (
              <div style={s.expiredBox}>
                <span>⏰ Han pasado más de 24h desde el último mensaje. WhatsApp no permite enviar texto libre.</span>
                <button onClick={retomarConversacion} disabled={retomando} style={s.dateApplyBtn}>
                  {retomando ? 'Enviando...' : '🔄 Retomar con plantilla'}
                </button>
              </div>
            ) : (
            <div style={s.inputArea}>
              <div style={s.inputInner}>
                <div style={s.inputBox}>
                  <textarea
                    ref={textareaRef}
                    style={s.textarea}
                    placeholder="Escribe un mensaje como asesor... (Enter para enviar)"
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                    }}
                    rows={2}
                  />
                  <button
                    style={{ ...s.sendBtn, opacity: !inputText.trim() || sending ? 0.4 : 1 }}
                    onClick={sendMessage}
                    disabled={!inputText.trim() || sending}
                  >
                    {sending ? '⏳' : '➤'}
                  </button>
                </div>
                <div style={s.inputNote}>
                  <span>⚡ Enter envía · Shift+Enter nueva línea</span>
                  <span style={{ color: selected?.canal === 'whatsapp' ? '#22c55e' : '#64748b' }}>
                    {selected?.canal === 'whatsapp' ? '📱 Se enviará por WhatsApp' : '⚠️ Solo se guarda en historial'}
                  </span>
                </div>
              </div>
            </div>
            )}
          </>
        )}
      </main>

      {showAddContact && (
        <div style={s.modalOverlay} onClick={() => setShowAddContact(false)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>+ Agregar contacto</div>
            <input
              style={s.modalInput}
              placeholder="Número (ej. 573024241107)"
              value={newContactNumero}
              onChange={e => setNewContactNumero(e.target.value)}
            />
            <input
              style={s.modalInput}
              placeholder="Nombre (opcional)"
              value={newContactNombre}
              onChange={e => setNewContactNombre(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={addContacto} disabled={savingContact} style={s.dateApplyBtn}>
                {savingContact ? 'Guardando...' : 'Guardar'}
              </button>
              <button onClick={() => setShowAddContact(false)} style={s.dateClearBtn}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────
function StatBox({ label, value, color }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', background: `${color}10`, borderRadius: 10, padding: '8px 4px', border: `1px solid ${color}20` }}>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value ?? '—'}</div>
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function DateSep({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 8px' }}>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.05)' }} />
      <span style={{ fontSize: 11, color: '#475569', background: 'rgba(255,255,255,0.04)', padding: '3px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.05)' }} />
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <div style={{ fontSize: 64, filter: 'grayscale(0.5)' }}>💬</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#334155', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Selecciona una conversación</div>
      <div style={{ fontSize: 14, color: '#1e293b' }}>Elige un chat de la lista para ver el historial y responder</div>
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────
const s = {
  root: { display: 'flex', height: '100vh', background: '#0d0d14', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#e2e8f0', overflow: 'hidden' },

  sidebar: { width: 340, minWidth: 280, display: 'flex', flexDirection: 'column', background: '#0f0f1a', borderRight: '1px solid rgba(255,255,255,0.05)' },
  sidebarTop: { padding: '18px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  brandLogo: { background: 'linear-gradient(135deg, #7c3aed, #ec4899)', borderRadius: 10, padding: '7px 12px', fontWeight: 800, fontSize: 13, color: '#fff', letterSpacing: 2, boxShadow: '0 4px 16px rgba(124,58,237,0.35)' },
  brandInfo: { flex: 1 },
  brandName: { fontSize: 14, fontWeight: 700, color: '#f1f5f9' },
  brandUser: { fontSize: 11, color: '#475569', marginTop: 1 },
  logoutBtn: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '6px 10px', color: '#f87171', cursor: 'pointer', fontSize: 14 },

  statsGrid: { display: 'flex', gap: 6, marginBottom: 10 },
  canalRow: { display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' },
  canalChip: { display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '4px 8px', fontSize: 12, border: '1px solid rgba(255,255,255,0.06)' },

  search: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)', padding: '8px 12px' },
  searchIco: { fontSize: 13, opacity: 0.5 },
  searchInput: { background: 'none', border: 'none', outline: 'none', color: '#e2e8f0', fontSize: 13, width: '100%', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  searchClear: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, padding: 0 },

  dateBtn: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontSize: 14 },
  dateBtnActive: { background: 'rgba(124,58,237,0.2)', border: '1px solid rgba(124,58,237,0.5)' },
  datePanel: { marginTop: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  dateRow: { display: 'flex', alignItems: 'center', gap: 8 },
  dateLabel: { fontSize: 11, color: '#94a3b8', width: 40, flexShrink: 0 },
  dateInput: { flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '5px 8px', color: '#e2e8f0', fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", colorScheme: 'dark' },
  dateActions: { display: 'flex', gap: 6, marginTop: 2 },
  dateApplyBtn: { flex: 1, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', border: 'none', borderRadius: 8, padding: '6px 0', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  dateClearBtn: { flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 0', color: '#94a3b8', fontSize: 12, cursor: 'pointer' },
  dateActiveChip: { marginTop: 8, fontSize: 11, color: '#c4b5fd', background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', width: 'fit-content' },

  viewTabs: { display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 4, marginBottom: 10 },
  viewTab: { flex: 1, background: 'none', border: 'none', color: '#64748b', fontSize: 12, fontWeight: 600, padding: '6px 0', borderRadius: 8, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  viewTabActive: { background: 'rgba(124,58,237,0.2)', color: '#e2e8f0' },
  addContactBtn: { width: '100%', background: 'linear-gradient(135deg, #7c3aed, #ec4899)', border: 'none', borderRadius: 10, padding: '9px 0', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  contactItem: { display: 'flex', gap: 10, padding: '10px 14px', alignItems: 'center' },
  contactAva: { width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 },
  contactDate: { fontSize: 11, color: '#475569', marginTop: 2 },
  contactInfo: { flex: 1, minWidth: 0 }, // ← permite que el texto se trunque en vez de empujar
  contactName: { fontSize: 13, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox: { background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: 20, width: 320 },
  modalTitle: { fontSize: 15, fontWeight: 800, color: '#f8fafc', marginBottom: 14 },
  modalInput: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, marginBottom: 8, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif" },

  tagFilterRow: { display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 },
  tagFilterChip: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#94a3b8', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  tagFilterChipActive: { background: 'rgba(124,58,237,0.2)', border: '1px solid rgba(124,58,237,0.5)', color: '#e2e8f0' },
  convTagsRow: { display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4, position: 'relative' },
  convTagChip: { fontSize: 10, borderRadius: 8, padding: '2px 6px', cursor: 'pointer', fontWeight: 600 },
  addTagBtn: { fontSize: 10, color: '#475569', cursor: 'pointer', padding: '2px 6px', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 8 },
  tagPicker: { position: 'absolute', top: '100%', left: 0, zIndex: 20, background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 6, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', minWidth: 140 },
  tagPickerItem: { fontSize: 12, color: '#e2e8f0', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' },

  convList: { flex: 1, overflowY: 'auto', padding: '6px 0' },
  emptyList: { textAlign: 'center', color: '#334155', fontSize: 13, padding: 28 },
  convItem: { display: 'flex', gap: 10, padding: '10px 14px', cursor: 'pointer', transition: 'background 0.15s' },
  convAva: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  convBody: { flex: 1, minWidth: 0 },
  convRow1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  convName: { fontSize: 13, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  convTime: { fontSize: 10, color: '#475569', flexShrink: 0, marginLeft: 6 },
  convRow2: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  convPrev: { fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  badge: { background: 'linear-gradient(135deg, #7c3aed, #ec4899)', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 7px', marginLeft: 6, flexShrink: 0 },
  canalTag: { fontSize: 10, fontWeight: 600, marginTop: 1 },

  wsBar: { padding: '8px 14px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 8 },
  wsDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  wsLabel: { fontSize: 11, color: '#475569' },

  main: { flex: 1, display: 'flex', flexDirection: 'column', background: '#0d0d14', overflow: 'hidden' },

  chatHeader: { padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(255,255,255,0.01)' },
  chatCanalBadge: { display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 10, flexShrink: 0 },
  chatHeaderMid: { flex: 1 },
  chatHeaderName: { fontSize: 16, fontWeight: 800, color: '#f8fafc', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  nombreInput: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 6, padding: '2px 8px', color: '#f8fafc', fontSize: 16, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none' },
  chatHeaderSub: { fontSize: 11, color: '#475569', marginTop: 2, fontFamily: "'JetBrains Mono', monospace" },
  chatHeaderRight: {},
  modoTag: { padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600 },

  messages: { flex: 1, overflowY: 'auto', padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 2 },
  loadingMsgs: { textAlign: 'center', color: '#334155', fontSize: 13, padding: 20 },

  msgRow: { display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 6 },
  avatar: { width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  bubble: { maxWidth: '65%', borderRadius: 16, padding: '10px 14px', wordBreak: 'break-word' },
  bubbleUser: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', borderBottomLeftRadius: 4 },
  bubbleAI: { background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', borderBottomRightRadius: 4 },
  bubbleAsesor: { background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderBottomRightRadius: 4 },
  bubbleWho: { fontSize: 10, fontWeight: 700, marginBottom: 5, letterSpacing: 0.5 },
  bubbleTxt: { fontSize: 14, lineHeight: 1.55, color: '#e2e8f0', whiteSpace: 'pre-wrap' },
  bubbleTs: { fontSize: 10, color: '#475569', marginTop: 5, textAlign: 'right' },

  inputArea: { borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)', padding: '12px 20px 16px' },
  inputInner: {},
  inputBox: { display: 'flex', gap: 10, alignItems: 'flex-end' },
  textarea: { flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 14, padding: '12px 16px', color: '#e2e8f0', fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none', resize: 'none', lineHeight: 1.5, maxHeight: 100, overflowY: 'auto' },
  sendBtn: { width: 46, height: 46, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #7c3aed, #ec4899)', color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s', flexShrink: 0, boxShadow: '0 4px 16px rgba(124,58,237,0.3)' },
  inputNote: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#334155', marginTop: 6, padding: '0 4px' },

  expiredBox: { display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', padding: '16px 20px', color: '#f59e0b', fontSize: 13, textAlign: 'center' },
  iniciarBtn: { background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 8, padding: '6px 10px', color: '#a78bfa', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' },

  deleteConvBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 12, color: '#475569', padding: '2px 4px',
    opacity: 0.6, transition: 'opacity 0.15s',
    marginTop: 2,
  },
  deleteMsgBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 11, color: '#475569', padding: '0 2px',
    opacity: 0, transition: 'opacity 0.2s',
  },

};
